import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import YouTubeModal from '../ui/YouTubeModal';

// Helper function to format numbers with spaces
const formatPrice = (price) => {
    if (price === null || price === undefined) return '';
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const ProductCard = ({ product, showButton = false }) => {
    const { t } = useTranslation();
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    
    const originalPrice = product.original_price ? parseFloat(product.original_price) : null;
    const discountPrice = product.discount_price ? parseFloat(product.discount_price) : null;
    const regularPrice = product.price ? parseFloat(product.price) : null;

    const hasDiscount = product.is_discounted && originalPrice && discountPrice;
    const discountPercentage = hasDiscount ? Math.round(((originalPrice - discountPrice) / originalPrice) * 100) : 0;

    return (
        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-200/50 h-full flex flex-col transform hover:-translate-y-2">
            {/* Image Section */}
            <div className="relative">
                <Link to={`/products/${product.id}`} className="block">
                    <div className="relative h-48 sm:h-52 lg:h-56 bg-slate-100">
                        {/* Image Loading Placeholder */}
                        {!imageLoaded && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-6 h-6 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin"></div>
                            </div>
                        )}
                        
                        <img
                            src={product.image1}
                            alt={product.name}
                            className={`w-full h-full object-contain p-3 sm:p-4 transition-all duration-300 group-hover:scale-105 ${
                                imageLoaded ? 'opacity-100' : 'opacity-0'
                            }`}
                            onLoad={() => setImageLoaded(true)}
                        />
                        
                        {/* Subtle overlay on hover */}
                        <div className="absolute inset-0 bg-blue-50/0 group-hover:bg-blue-50/30 transition-all duration-300 rounded-t-2xl"></div>
                    </div>
                </Link>

                {/* YouTube Video Play Button - Modern and Subtle */}
                {product.youtube_url && (
                    <div className="absolute bottom-3 right-3">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsVideoModalOpen(true);
                            }}
                            className="group/play flex items-center justify-center w-8 h-8 bg-white/90 backdrop-blur-sm hover:bg-white text-slate-700 hover:text-red-600 rounded-full transition-all duration-300 transform hover:scale-110 shadow-md hover:shadow-lg border border-white/50"
                            title={t('common.watchVideo') || 'Videoni ko\'rish'}
                        >
                            <svg className="w-3.5 h-3.5 ml-0.5 transition-all duration-200 group-hover/play:scale-110" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                            </svg>
                        </button>
                    </div>
                )}

                {/* Discount Badge */}
                {hasDiscount && (
                    <div className="absolute top-3 right-3">
                        <span className="bg-red-500 text-white text-xs sm:text-sm font-bold px-2 py-1 rounded-lg shadow-md">
                            -{discountPercentage}%
                        </span>
                    </div>
                )}

                {/* Stock Status Overlay */}
                {product.stock_quantity <= 0 && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="bg-red-500 text-white px-3 sm:px-4 py-2 rounded-lg text-sm font-bold shadow-lg">
                            {t('common.outOfStock') || 'Tugandi'}
                        </span>
                    </div>
                )}
            </div>

            {/* Content Section */}
            <div className="p-4 sm:p-5 lg:p-6 flex flex-col flex-grow">
                {/* Category Badge */}
                {product.category && (
                    <div className="mb-3">
                        <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-2 sm:px-3 py-1 rounded-full">
                            {product.category.name}
                        </span>
                    </div>
                )}

                {/* Product Title */}
                <h3 className="font-bold text-base sm:text-lg text-slate-900 mb-3 line-clamp-2 flex-grow group-hover:text-blue-600 transition-colors duration-300">
                    <Link to={`/products/${product.id}`} className="hover:text-blue-600 transition-colors duration-200">
                        {product.name}
                    </Link>
                </h3>

                {/* Price Section */}
                <div className="mb-4">
                    {hasDiscount ? (
                        <div className="space-y-1">
                            <p className="text-sm text-slate-400 line-through">
                                {formatPrice(originalPrice)} so'm
                            </p>
                            <div className="flex items-baseline space-x-2 flex-wrap">
                                <p className="text-lg sm:text-xl font-bold text-blue-600">
                                    {formatPrice(discountPrice)} so'm
                                </p>
                                <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded">
                                    -{formatPrice(originalPrice - discountPrice)} tejash
                                </span>
                            </div>
                        </div>
                    ) : (
                        <p className="text-lg sm:text-xl font-bold text-blue-600">
                            {formatPrice(regularPrice)} so'm
                        </p>
                    )}
                </div>

                {/* Stock Status & Action */}
                <div className="flex items-center justify-between gap-3">
                    {/* Stock Indicator */}
                    <div className="flex items-center space-x-2 flex-shrink-0">
                        <div className={`w-2 h-2 rounded-full ${product.stock_quantity > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className={`text-xs sm:text-sm font-medium ${product.stock_quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {product.stock_quantity > 0 ? t('common.inStock') || 'Mavjud' : t('common.outOfStock') || 'Tugandi'}
                        </span>
                    </div>
                    
                    {/* Action Button */}
                    {showButton && (
                        <Link 
                            to={`/products/${product.id}`}
                            className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex-shrink-0"
                        >
                            <span className="mr-1">{t('pages.products.viewDetails') || 'Batafsil'}</span>
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </Link>
                    )}
                </div>
            </div>

            {/* YouTube Video Modal */}
            <YouTubeModal 
                isOpen={isVideoModalOpen}
                onClose={() => setIsVideoModalOpen(false)}
                videoUrl={product.youtube_url}
                productName={product.name}
            />

            <style jsx>{`
                .line-clamp-2 {
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 2;
                }
            `}</style>
        </div>
    );
};

export default ProductCard;
